'use client'

import { Button } from '@payloadcms/ui'
import Link from 'next/link'
import { useRef } from 'react'
import { toast } from 'sonner'

import { useLocale } from '@/components/LocaleContext'
import { ZoomableImage } from '@/components/frontend/ZoomableImage'
import { isLoggedInClient } from '@/lib/auth'
import { buildProductDetailUrl, getProductDetailSlug } from '@/lib/productUrls'
import { emitCartUpdated } from '@/utilities/eventEmitter'
import { getPrice } from '@/utilities/formatPrice'

export type ProductCardProps = {
  id?: string
  name: string
  imageUrl: string | null
  imageAlt: string
  currentPriceFormatted: string
  originalPriceFormatted: string | null
  sku: string | null
  discountPercentage: number | null
  showOriginalPrice: boolean
  inStock?: boolean
  stockQty?: number
  partNumber: string | null
  matchReason?: any[] | null
  item_group?: string | null
  detailSlug?: string
}

const messages = {
  en: {
    failedUpdateCart: 'Failed to update cart',
    updatingCart: 'Updating cart…',
    addedToCart: 'Added to cart',
    addToCart: 'ADD TO CART',
    outOfStock: 'Out of Stock',
    singleQtyAlreadyInCart:
      'This product has only 1 in stock. You have already added it to your cart.',
  },
  fr: {
    failedUpdateCart: 'Échec de mise à jour du panier',
    updatingCart: 'Mise à jour du panier…',
    addedToCart: 'Ajouté au panier',
    addToCart: 'Ajouter au panier',
    outOfStock: 'Rupture de stock',
    singleQtyAlreadyInCart:
      'Ce produit n’a qu’une unité en stock. Vous l’avez déjà ajouté à votre panier.',
  },
  ar: {
    failedUpdateCart: 'فشل تحديث السلة',
    updatingCart: 'جارٍ تحديث السلة…',
    addedToCart: 'تمت إضافته إلى السلة',
    addToCart: 'أضف إلى السلة',
    outOfStock: 'غير متوفر',
    singleQtyAlreadyInCart: 'هذا المنتج متوفر بكمية واحدة فقط. لقد أضفته بالفعل إلى سلة التسوق.',
  },
}

export function ProductCard({
  id,
  name,
  imageUrl,
  imageAlt,
  currentPriceFormatted,
  originalPriceFormatted,
  sku,
  discountPercentage,
  showOriginalPrice,
  inStock = true,
  stockQty,
  partNumber,
  matchReason,
  detailSlug,
}: ProductCardProps) {
  const { locale } = useLocale()
  const toastText = messages[locale] ?? messages.en
  const hasDiscount = discountPercentage != null && discountPercentage > 0
  const isInStock = stockQty != null ? stockQty > 0 : inStock

  const cardWidthPx = 260
  const cardHeightPx = 400
  const imageHeightPx = 220

  const detailUrl =
    id != null && String(id).trim() !== ''
      ? buildProductDetailUrl(
          detailSlug ?? getProductDetailSlug({ id: String(id), partNumber, sku }),
        )
      : null

  const isDealer = isLoggedInClient()
  const cartQtyRef = useRef(0)

  const match = matchReason
    ? matchReason
        ?.filter(({ brand }) => brand?.toLowerCase() !== 'gallium')
        ?.map(({ matchedValue, brand }) => `  ${matchedValue} ${brand ? `(${brand}) \n` : ''}`)
    : []

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const cartCode = sku ?? partNumber ?? id

    if (!id || !cartCode) {
      toast.error(toastText.failedUpdateCart)
      return
    }

    if (isDealer && stockQty === 1 && cartQtyRef.current >= 1) {
      toast.warning(toastText.singleQtyAlreadyInCart)
      return
    }

    const toastId = toast.loading(toastText.updatingCart)

    try {
      const response = await fetch('/api/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: id,
          qty: 1,
          code: cartCode,
          rate: currentPriceFormatted,
          enforceStockLimit: isDealer,
        }),
      })

      if (response.status === 409) {
        const data = (await response.json().catch(() => ({}))) as {
          code?: string
          available?: number
          currentCartQty?: number
          error?: string
        }

        if (data.code === 'INSUFFICIENT_STOCK') {
          const available = data.available ?? stockQty ?? 0
          const currentCartQty = Number.isInteger(data.currentCartQty)
            ? data.currentCartQty!
            : cartQtyRef.current

          cartQtyRef.current = currentCartQty

          if (available === 1 && currentCartQty >= 1) {
            toast.warning(toastText.singleQtyAlreadyInCart, { id: toastId })
          } else {
            toast.warning(data.error ?? toastText.failedUpdateCart, { id: toastId })
          }
          return
        }
      }

      if (!response.ok) {
        throw new Error('Add to cart failed')
      }

      cartQtyRef.current += 1
      emitCartUpdated()
      const label = partNumber ?? sku ?? name
      toast.success(`${label} ${toastText.addedToCart}`, { id: toastId })
    } catch {
      toast.error(toastText.failedUpdateCart, { id: toastId })
    }
  }

  const price = getPrice(currentPriceFormatted)
  const canAddToCart = !isDealer || (isDealer && isInStock && Boolean(price))

  return (
    <article
      className="group overflow-hidden rounded-md bg-white shadow-sm transition-shadow hover:shadow-md flex flex-col"
      style={{ width: cardWidthPx, height: cardHeightPx }}
    >
      {detailUrl ? (
        <Link
          href={detailUrl}
          className="flex flex-col flex-1 rounded-md focus:outline-none min-h-0"
          aria-label={name}
        >
          <div
            className="relative w-full overflow-hidden flex items-center justify-center"
            style={{ height: imageHeightPx }}
          >
            {imageUrl ? (
              <div
                className="relative flex items-center justify-center transition-transform group-hover:scale-105"
                style={{ width: '100%', height: '100%' }}
              >
                <ZoomableImage
                  src={imageUrl}
                  alt={imageAlt}
                  fill
                  className="object-contain object-center"
                  sizes="260px"
                  unoptimized={imageUrl.startsWith('http')}
                  stopPropagation
                />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-neutral-500">
                <ZoomableImage
                  src="/no-image.webp"
                  alt="No image"
                  fill
                  className="object-contain object-center"
                  sizes="260px"
                  unoptimized={true}
                  stopPropagation
                />
              </div>
            )}
            {/* {isDealer && hasDiscount && (
              <span className="absolute left-2 top-2 rounded bg-[#111] px-2 py-1 font-poppins text-xs font-semibold text-white">
                -{discountPercentage}%
              </span>
            )} */}
            {isDealer && !isInStock && (
              <span className="absolute left-2 top-2 rounded bg-[#FF0000] px-2 py-1 font-poppins text-xs font-semibold uppercase text-white">
                {toastText.outOfStock}
              </span>
            )}
            {/* <div
              className="absolute right-1.5 top-1.5 flex flex-col gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-full border border-neutral-300 bg-white shadow-sm hover:bg-neutral-50"
                aria-label="Quick view"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <EyeIcon />
              </button>
            </div> */}
          </div>
          <div className="pt-0 pb-3 min-h-[62px]" onClick={(e) => e.stopPropagation()}>
            {canAddToCart ? (
              <Button
                buttonStyle="none"
                className="block w-full rounded-none border-0 bg-[#111] py-3 font-poppins text-center text-sm font-bold uppercase tracking-wide text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 hover:opacity-100 focus:opacity-100 focus-within:opacity-100 no-underline"
                onClick={handleAddToCart}
              >
                {toastText.addToCart}
              </Button>
            ) : null}
          </div>
          <div className="px-4 py-3 pt-0 flex-1">
            {partNumber && (
              <p className="font-poppins text-lg font-bold uppercase tracking-wider text-neutral-500">
                {partNumber}
              </p>
            )}

            <div
              dir={locale === 'ar' ? 'rtl' : 'ltr'}
              className="mt-1.5 flex flex-wrap items-baseline gap-2"
            >
              {isDealer && price && (
                <span
                  className={`font-poppins text-base font-semibold ${locale == 'ar' ? 'text-right' : ''} ${isInStock ? 'text-[#FF0000]' : 'text-neutral-500'}`}
                >
                  ${currentPriceFormatted}
                </span>
              )}
            </div>
            <p
              className={`font-bebas-neue mt-1 text-sm tracking-[1px] font-medium uppercase leading-tight ${
                isInStock ? 'text-[#111] group-hover:text-[#FF0000]' : 'text-neutral-500'
              }`}
            >
              {name}
            </p>
            {!!match && match?.length > 0 && (
              <p className="mt-1 text-xs text-[#6f0b00] line-clamp-2" title={match?.join(' , ')}>
                {match?.join(',\n')}
              </p>
            )}
          </div>
        </Link>
      ) : (
        <>
          <div
            className="relative w-full overflow-hidden bg-neutral-200 flex items-center justify-center"
            style={{ height: imageHeightPx }}
          >
            {imageUrl ? (
              <div
                className="relative flex items-center justify-center transition-transform group-hover:scale-105"
                style={{ width: '100%', height: '100%' }}
              >
                <ZoomableImage
                  src={imageUrl}
                  alt={imageAlt}
                  fill
                  className="object-cover object-center"
                  sizes="260px"
                  unoptimized={imageUrl.startsWith('http')}
                />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-neutral-500">
                <ZoomableImage
                  src="/no-image.webp"
                  alt="No image"
                  fill
                  className="object-contain object-center"
                  sizes="260px"
                  unoptimized={true}
                  stopPropagation
                />
              </div>
            )}
            {hasDiscount && (
              <span className="absolute left-2 top-2 rounded bg-[#111] px-2 py-1 font-poppins text-xs font-semibold text-white">
                -{discountPercentage}%
              </span>
            )}
            {!isInStock && (
              <span className="absolute left-2 top-2 rounded bg-[#FF0000] px-2 py-1 font-poppins text-xs font-semibold uppercase text-white">
                {toastText.outOfStock}
              </span>
            )}
          </div>
          <div className="pt-0 pb-3 min-h-[62px]">
            {isInStock && detailUrl && price ? (
              <Link
                href={detailUrl}
                className="block w-full rounded-none border-0 bg-[#111] py-3 font-poppins text-center text-sm font-bold uppercase tracking-wide text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 hover:opacity-100 focus:opacity-100 focus-within:opacity-100 no-underline"
              >
                {toastText.addToCart}
              </Link>
            ) : null}
          </div>
          <div className="px-4 py-3 pt-0">
            {partNumber && (
              <p className="font-poppins mt-1 text-lg font-bold uppercase tracking-wider text-neutral-500">
                {partNumber}
              </p>
            )}

            <div
              dir={locale == 'ar' ? 'rtl' : 'ltr'}
              className="mt-1.5 flex flex-wrap items-baseline gap-2"
            >
              {isDealer && price && (
                <span
                  className={`font-poppins text-base font-semibold ${locale == 'ar' ? 'text-right' : ''} ${isInStock ? 'text-[#FF0000]' : 'text-neutral-500'}`}
                >
                  {currentPriceFormatted}
                </span>
              )}
              {isDealer && showOriginalPrice && originalPriceFormatted && (
                <span className="font-poppins text-sm text-neutral-500 line-through">
                  {originalPriceFormatted}
                </span>
              )}
            </div>
            <p
              className={`font-bebas-neue text-sm font-medium uppercase leading-tight ${isInStock ? 'text-[#111] group-hover:text-[#FF0000]' : 'text-neutral-500'}`}
            >
              {name}
            </p>
          </div>
        </>
      )}
    </article>
  )
}
